import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function _callSuper(_this, derived, args) {
  function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
      return !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    } catch (e) {
      return false;
    }
  }
  derived = _getPrototypeOf(derived);
  return _possibleConstructorReturn(_this, isNativeReflectConstruct() ? Reflect.construct(derived, args || [], _getPrototypeOf(_this).constructor) : derived.apply(_this, args));
}
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormFieldGroup } from '@instructure/quiz-common';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { View } from '@instructure/ui-view';
import { List } from 'immutable';
import { breakdownSeconds, DAY_IN_SECONDS, HOUR_IN_SECONDS, MINUTE_IN_SECONDS } from '../FormattedDuration';
import t from '@instructure/quiz-i18n/format-message';
import { NumberInput } from '@instructure/quiz-number-input';

// This component renders an input for multiple time units, and handles:
// * converting smaller units to larger units when over the unit threshold (e.g. 95 minutes to 1h35m)
// * displaying feedback messages when unit values are over a unit threshold
// * displaying feedback messages when user input is out-of-range
// * displaying error messages when user input is invalid
// * calling onChange with a value which is always valid and in-range
//
// Currently only minutes/hours/days are supported, but support for
// seconds could be added in the future if needed

export var TimeUnitsInput = /*#__PURE__*/function (_Component) {
  function TimeUnitsInput() {
    var _this2;
    _classCallCheck(this, TimeUnitsInput);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this2 = _callSuper(this, TimeUnitsInput, [].concat(args));
    _defineProperty(_this2, "isIncrementOrDecrement", function (e) {
      // Is the event an increment or decrement event?
      // These events can be sourced from either the arrow buttons on the input, or the user
      // using the up or down arrows on the keyboard
      var fromButtonInput = e.nativeEvent.target && e.nativeEvent.target.tagName !== 'INPUT';
      var fromArrowKeys = e.key === 'ArrowUp' || e.key === 'ArrowDown';
      return fromButtonInput || fromArrowKeys;
    });
    _defineProperty(_this2, "onChangeTime", function (e, rawString, inputNumber, source) {
      var normalizeOnInput = _this2.isIncrementOrDecrement(e) || _this2.props.normalizeOnInput;
      var minutes = source === 'minutes' ? inputNumber : _this2.state.minutes;
      minutes = normalizeOnInput ? minutes : Math.max(minutes, 0);
      var hours = source === 'hours' ? inputNumber : _this2.state.hours;
      hours = normalizeOnInput ? hours : Math.max(hours, 0);
      var days = source === 'days' ? inputNumber : _this2.state.days || 0;
      var totalSeconds = minutes * MINUTE_IN_SECONDS + hours * HOUR_IN_SECONDS + days * DAY_IN_SECONDS;
      var hoursError = null;
      var daysError = null;
      var shouldResetOnNormalize = false;
      var _breakdownSeconds = breakdownSeconds(totalSeconds, _this2.props.includeDays),
        newDays = _breakdownSeconds.days,
        newHours = _breakdownSeconds.hours,
        newMinutes = _breakdownSeconds.minutes;
      if (totalSeconds > _this2.props.maxTimeInSeconds) {
        var _breakdownSeconds2 = breakdownSeconds(_this2.props.maxTimeInSeconds, _this2.props.includeDays),
          maxDays = _breakdownSeconds2.days,
          maxHours = _breakdownSeconds2.hours,
          maxMinutes = _breakdownSeconds2.minutes;
        if (_this2.props.includeDays) {
          daysError = t('{newDays, number} days {newHours, number} hours {newMinutes, number} minutes exceeds maximum time of {maxDays, number} days {maxHours, number} hours {maxMinutes, number} minutes.', {
            newDays: newDays,
            newHours: newHours,
            newMinutes: newMinutes,
            maxDays: maxDays,
            maxHours: maxHours,
            maxMinutes: maxMinutes
          });
        } else {
          hoursError = t('{newHours, number} hours {newMinutes, number} minutes exceeds maximum time of {maxHours, number} hours {maxMinutes, number} minutes.', {
            newHours: newHours,
            newMinutes: newMinutes,
            maxHours: maxHours,
            maxMinutes: maxMinutes
          });
        }
        totalSeconds = _this2.props.maxTimeInSeconds;
        shouldResetOnNormalize = true;
      } else if (totalSeconds < _this2.props.minTimeInSeconds) {
        var _breakdownSeconds3 = breakdownSeconds(_this2.props.minTimeInSeconds, _this2.props.includeDays),
          minDays = _breakdownSeconds3.days,
          minHours = _breakdownSeconds3.hours,
          minMinutes = _breakdownSeconds3.minutes;
        if (_this2.props.includeDays) {
          daysError = t('{newDays, number} days {newHours, number} hours {newMinutes, number} minutes is lower than the minimum time of {minDays, number} days {minHours, number} hours {minMinutes, number} minutes.', {
            newDays: newDays,
            newHours: newHours,
            newMinutes: newMinutes,
            minDays: minDays,
            minHours: minHours,
            minMinutes: minMinutes
          });
        } else {
          hoursError = t('{newHours, number} hours {newMinutes, number} minutes is lower than the minimum time of {minHours, number} hours {minMinutes, number} minutes.', {
            newHours: newHours,
            newMinutes: newMinutes,
            minHours: minHours,
            minMinutes: minMinutes
          });
        }
        totalSeconds = _this2.props.minTimeInSeconds;
        shouldResetOnNormalize = true;
      }
      if (normalizeOnInput) {
        _this2.setState({
          days: days,
          hours: hours,
          minutes: minutes,
          daysError: daysError,
          hoursError: hoursError,
          minutesError: null,
          totalSeconds: totalSeconds,
          shouldResetOnNormalize: shouldResetOnNormalize
        }, _this2.onNormalizeTime);
      } else {
        _this2.setState({
          days: days,
          rawDaysText: days,
          hours: hours,
          rawHoursText: hours,
          minutes: minutes,
          rawMinutesText: minutes,
          daysError: daysError,
          hoursError: hoursError,
          minutesError: null,
          totalSeconds: totalSeconds,
          shouldResetOnNormalize: shouldResetOnNormalize
        });
      }
    });
    _defineProperty(_this2, "onNormalizeTime", function () {
      var _breakdownSeconds4 = breakdownSeconds(_this2.state.totalSeconds, _this2.props.includeDays),
        newDays = _breakdownSeconds4.days,
        newHours = _breakdownSeconds4.hours,
        newMinutes = _breakdownSeconds4.minutes;
      if (_this2.state.shouldResetOnNormalize) {
        var _this2$props$onReset, _this2$props;
        (_this2$props$onReset = (_this2$props = _this2.props).onReset) === null || _this2$props$onReset === void 0 || _this2$props$onReset.call(_this2$props);
      }
      _this2.setState({
        days: newDays,
        rawDaysText: newDays,
        hours: newHours,
        rawHoursText: newHours,
        minutes: newMinutes,
        rawMinutesText: newMinutes,
        shouldResetOnNormalize: false
      });
      _this2.props.onChange(_this2.state.totalSeconds);
    });
    _defineProperty(_this2, "onChangeExtraMinutes", function (e, rawString, normalizedString) {
      _this2.onChangeTimeUnit(e, rawString, normalizedString, 'minutes');
    });
    _defineProperty(_this2, "onChangeExtraHours", function (e, rawString, normalizedString) {
      _this2.onChangeTimeUnit(e, rawString, normalizedString, 'hours');
    });
    _defineProperty(_this2, "onChangeExtraDays", function (e, rawString, normalizedString) {
      _this2.onChangeTimeUnit(e, rawString, normalizedString, 'days');
    });
    _defineProperty(_this2, "getErrorList", function () {
      var errorArray = [];
      var _this2$state = _this2.state,
        daysError = _this2$state.daysError,
        hoursError = _this2$state.hoursError,
        minutesError = _this2$state.minutesError;
      daysError && errorArray.push(daysError);
      hoursError && errorArray.push(hoursError);
      minutesError && errorArray.push(minutesError);
      return errorArray.map(function (error) {
        return {
          type: 'error',
          text: error
        };
      });
    });
    return _this2;
  }
  _inherits(TimeUnitsInput, _Component);
  return _createClass(TimeUnitsInput, [{
    key: "UNSAFE_componentWillMount",
    value: function UNSAFE_componentWillMount() {
      var _breakdownSeconds5 = breakdownSeconds(this.props.timeInSeconds, this.props.includeDays),
        days = _breakdownSeconds5.days,
        hours = _breakdownSeconds5.hours,
        minutes = _breakdownSeconds5.minutes;
      this.setState({
        days: days,
        hours: hours,
        minutes: minutes,
        rawDaysText: days,
        rawHoursText: hours,
        rawMinutesText: minutes,
        totalSeconds: this.props.timeInSeconds,
        shouldResetOnNormalize: false,
        errors: List()
      });
    }
  }, {
    key: "onChangeTimeUnit",
    value: function onChangeTimeUnit(e, rawString, normalizedString, unit) {
      // Check that input is a valid number.
      // If not, set an error indicator. If so, remove error indicator.
      var rawStringBlank = rawString === '';
      var inputNumber = normalizedString === null ? NaN : Number(normalizedString);
      var numberInvalid = isNaN(inputNumber);
      if (this.props.normalizeOnInput && rawStringBlank) {
        // When normalizeOnInput is true, treat blank strings as 0,
        // otherwise it's really hard to clear the input and enter a new number
        inputNumber = 0;
        numberInvalid = false;
      }
      if (numberInvalid) {
        if (this.props.normalizeOnInput) {
          this.onNormalizeTime();
          return;
        }
        var unitMessageText = t('{userInput, string} is not a valid number.', {
          userInput: rawString
        });
        var update = {};
        if (unit === 'minutes') {
          update.rawMinutesText = rawString;
          update.minutesError = rawStringBlank ? null : unitMessageText;
        } else if (unit === 'hours') {
          update.rawHoursText = rawString;
          update.hoursError = rawStringBlank ? null : unitMessageText;
        } else if (unit === 'days') {
          update.rawDaysText = rawString;
          update.daysError = rawStringBlank ? null : unitMessageText;
        }
        this.setState(update);
      } else {
        this.onChangeTime(e, rawString, inputNumber, unit);
      }
    }
  }, {
    key: "render",
    value: function render() {
      return /*#__PURE__*/React.createElement(View, {
        display: "block"
      }, /*#__PURE__*/React.createElement(FormFieldGroup, {
        description: /*#__PURE__*/React.createElement(ScreenReaderContent, null, this.props.description),
        layout: "columns",
        vAlign: "top",
        startAt: "small",
        messages: this.getErrorList()
      }, this.props.includeDays && /*#__PURE__*/React.createElement(NumberInput, {
        renderLabel: this.props.daysLabel,
        min: 0,
        value: this.state.rawDaysText,
        onChange: this.onChangeExtraDays,
        onBlur: this.onNormalizeTime,
        messages: this.state.daysError ? [{
          type: 'error'
        }] : null,
        "data-automation": this.props.daysDataAutomation,
        width: this.props.inputWidth
      }), /*#__PURE__*/React.createElement(NumberInput, {
        renderLabel: this.props.hoursLabel,
        min: this.props.includeDays ? undefined : 0,
        value: this.state.rawHoursText,
        onChange: this.onChangeExtraHours,
        onBlur: this.onNormalizeTime,
        messages: this.state.hoursError ? [{
          type: 'error'
        }] : null,
        "data-automation": this.props.hoursDataAutomation,
        width: this.props.inputWidth
      }), /*#__PURE__*/React.createElement(NumberInput, {
        renderLabel: this.props.minutesLabel,
        value: this.state.rawMinutesText,
        onChange: this.onChangeExtraMinutes,
        onBlur: this.onNormalizeTime,
        messages: this.state.minutesError ? [{
          type: 'error'
        }] : null,
        "data-automation": this.props.minutesDataAutomation,
        width: this.props.inputWidth
      })));
    }
  }]);
}(Component);
_defineProperty(TimeUnitsInput, "propTypes", {
  onChange: PropTypes.func.isRequired,
  margin: PropTypes.string,
  inputWidth: PropTypes.string,
  description: PropTypes.string.isRequired,
  timeInSeconds: PropTypes.number,
  includeDays: PropTypes.bool,
  daysLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  hoursLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  minutesLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  daysDataAutomation: PropTypes.string,
  hoursDataAutomation: PropTypes.string,
  minutesDataAutomation: PropTypes.string,
  maxTimeInSeconds: PropTypes.number.isRequired,
  minTimeInSeconds: PropTypes.number,
  normalizeOnInput: PropTypes.bool,
  onReset: PropTypes.func
  // Should the number inputs normalize on every input event, instead of waiting for blur event?
  // Note: increment actions (e.g. arrow up) will always normalize immediately
  // Note: onChange will only ever be called after normalization
});
_defineProperty(TimeUnitsInput, "defaultProps", {
  includeDays: false,
  daysLabel: t('Days'),
  hoursLabel: t('Hours'),
  minutesLabel: t('Minutes'),
  timeInSeconds: 0,
  minTimeInSeconds: 0,
  normalizeOnInput: false,
  margin: undefined,
  daysDataAutomation: '',
  hoursDataAutomation: '',
  minutesDataAutomation: '',
  inputWidth: null,
  onReset: void 0
});
export default TimeUnitsInput;